import React, { useState, useEffect, useMemo } from 'react';
import './AnalysisDashboard.css';
import { transformProgressData, SERVICE_DISPLAY_NAMES, SERVICE_ORDER } from '../../../utils/progressDataTransformer';

const RSA_MODEL_PROJECT_TYPES = new Set(['rsa-model', 'rsa-existing', 'rsa_existing']);

// Phase C-1: deep links into AWS console for ops debugging.
// run_id IS the SF execution name (engine=v2 dispatch sets it that way), so
// the execution ARN is deterministic and the SF console URL is shareable.
const AWS_REGION = 'us-east-1';
const AWS_ACCOUNT = '401251961508';
const SF_STATE_MACHINE_NAME = 'rsa-chain';

const buildSfConsoleUrl = (runId) => {
    if (!runId) return null;
    const arn = `arn:aws:states:${AWS_REGION}:${AWS_ACCOUNT}:execution:${SF_STATE_MACHINE_NAME}:${runId}`;
    // SF console uses URL-encoded ARN as the state path.
    return `https://${AWS_REGION}.console.aws.amazon.com/states/home?region=${AWS_REGION}#/v2/executions/details/${encodeURIComponent(arn)}`;
};

// CloudWatch Logs Insights URL for a list of log groups, filtered by run_id.
// `groups` is a list of log group names (e.g. ['/aws/batch/rsa-tiling']).
// CloudWatch's URL scheme uses a tilde-encoded JSON object; we keep the
// query short on purpose (one filter line) so the encoding stays manageable.
const buildLogsInsightsUrl = (runId, groups) => {
    if (!runId || !groups || groups.length === 0) return null;
    const editor = `fields @timestamp, @message\n| filter @message like /${runId}/\n| sort @timestamp desc\n| limit 200`;
    const sources = groups.map((g) => `'${g}`).join('~');
    // CloudWatch's "anchor" format: tilde-encoded compact JSON-like.
    const detail = `~(end~0~start~-86400~timeType~'RELATIVE~unit~'seconds~editorString~'${encodeURIComponent(editor).replace(/%/g, '*')}~source~(~${sources}))`;
    return `https://${AWS_REGION}.console.aws.amazon.com/cloudwatch/home?region=${AWS_REGION}#logsV2:logs-insights$3FqueryDetail$3D${detail}`;
};

// Per-step → log groups. Two zoom levels:
//   - Phase keys (file_transfer, rsa_analysis, final_reports) match the
//     grouped step names emitted by buildRsaModelOverview. The phase-tile
//     header's "View logs" link uses these and surfaces all workers in
//     that phase under one CloudWatch query.
//   - Per-service keys (tiling, csf, road_detection, ...) match the raw
//     service_name values emitted by the worker progress callbacks. The
//     expanded sub-stage row's "View logs" link uses these for a focused
//     single-stage CloudWatch query.
// create_rsa is intentionally absent — it runs as a Lambda waitForTaskToken,
// not Batch, so /aws/batch/rsa-create_rsa doesn't exist. SF console link is
// the right entry point for that step.
const STEP_LOG_GROUPS = {
    // phase-level (grouped tile header)
    file_transfer: [
        '/aws/batch/rsa-tiling',
        '/aws/batch/rsa-csf',
        '/aws/batch/rsa-road_detection',
        '/aws/batch/rsa-car_removal',
        '/aws/batch/rsa-file_transfer',
    ],
    rsa_analysis: [
        '/aws/batch/rsa-ssd_classification',
        '/aws/batch/rsa-shapefile',
    ],
    final_reports: [
        '/aws/batch/rsa-automatic_reports',
    ],
    // per-service (expanded sub-stage rows)
    tiling: ['/aws/batch/rsa-tiling'],
    csf: ['/aws/batch/rsa-csf'],
    road_detection: ['/aws/batch/rsa-road_detection'],
    car_removal: ['/aws/batch/rsa-car_removal'],
    ssd_classification: ['/aws/batch/rsa-ssd_classification'],
    psd_classification: ['/aws/batch/rsa-psd_classification'],
    shapefile: ['/aws/batch/rsa-shapefile'],
    automatic_reports: ['/aws/batch/rsa-automatic_reports'],
    automatic_report: ['/aws/batch/rsa-automatic_reports'],
    report_generation: ['/aws/batch/rsa-automatic_reports'],
};

/** Google Drive analysis template IDs → labels (aligned with RSAAnalysis RSA_MODEL_ANALYSIS_TEMPLATE_ID_MAPPING). */
const RSA_MODEL_TEMPLATE_ID_TO_LABEL = {
    '1utdS8VLw2hoCf00xZbXhYtcTsWnWDdsm': 'Intersection Sight Distance (ISD)',
    '10bkKVtyerK5Qe20g7Rc15ncwDQn4daih': 'Passing Sight Distance (PSD)',
    '1rTfTDLA48ET--fAduVGsI0ad3LFssmLC': 'Stopping Sight Distance (SSD)',
    '1UATrkaa_ASvlJejyfi0TekiM3nBfO9lC': 'Flat Spot Analysis (FSA)',
    '1irrUhf9npguUDAxHy29tILDiv7DTVO0t': 'Sunglare Analysis',
    '1ElRBuXgNAHakGVVw11671QAn34IeQQKN': 'Horizontal Alignment',
};

const RSA_MODEL_ANALYSIS_TYPE_TOKEN_TO_LABEL = {
    SSD: 'Stopping Sight Distance (SSD)',
    PSD: 'Passing Sight Distance (PSD)',
    FSA: 'Flat Spot Analysis (FSA)',
    ISD: 'Intersection Sight Distance (ISD)',
    GLR: 'Sunglare Analysis',
    Horizontal: 'Horizontal Alignment',
    HORIZONTAL: 'Horizontal Alignment',
};

/**
 * Human-readable analyses from the rsa-model launch payload (or optimistic selected task names).
 * Backend stores `metadata.launch_payload` with `analysis_template_ids`; `metadata.analysis_types` is a fallback.
 */
const getRsaModelPayloadAnalysisLabels = (run) => {
    if (!run) return [];
    const launch = run.metadata?.launch_payload;
    const ids = launch?.analysis_template_ids;
    if (Array.isArray(ids) && ids.length > 0) {
        return ids.map((id) => {
            const key = String(id ?? '').trim();
            return RSA_MODEL_TEMPLATE_ID_TO_LABEL[key] || key;
        });
    }
    const analysisTypes = run.metadata?.analysis_types;
    if (Array.isArray(analysisTypes) && analysisTypes.length > 0) {
        return analysisTypes.map((t) => {
            const s = String(t ?? '').trim();
            if (!s) return '';
            return (
                RSA_MODEL_ANALYSIS_TYPE_TOKEN_TO_LABEL[s] ||
                RSA_MODEL_ANALYSIS_TYPE_TOKEN_TO_LABEL[s.toUpperCase()] ||
                s
            );
        }).filter(Boolean);
    }
    const svc = run.services;
    if (!Array.isArray(svc) || svc.length === 0) return [];
    const looksLikePipelineKey = (str) => {
        const s = String(str || '').trim();
        if (!s) return false;
        if (s.includes('(') || /sight distance|analysis/i.test(s)) return false;
        return /^[a-z][a-z0-9_]*$/i.test(s) && s.includes('_');
    };
    if (svc.some(looksLikePipelineKey)) return [];
    return svc.map((s) => String(s).trim()).filter(Boolean);
};

// engine=v2 emits its chunk-parallel preprocessing stages (tiling, csf,
// road_detection, car_removal, file_transfer) as separate services. They
// all conceptually belong to the "File Transfer / Preprocessing" bucket
// in the user's mental model. engine=v1 emits only `file_transfer` for
// the same logical stage.
const RSA_MODEL_STEP_GROUPS = [
    {
        name: 'file_transfer',
        display_name: 'File Transfer',
        description: 'Preparing and transferring files for processing',
        defaultProgress: 20,
        services: [
            'tiling',
            'csf',
            'road_detection',
            'car_removal',
            'file_transfer'
        ]
    },
    {
        name: 'rsa_analysis',
        display_name: 'RSA Analysis',
        description: 'Running RSA and analysis services',
        defaultProgress: 55,
        services: [
            'create_rsa',
            'ssd_classification',
            'psd_classification',
            'horizontal',
            'embankment',
            'clear_zone',
            'guardrail',
            'shapefile'
        ]
    },
    {
        name: 'final_reports',
        display_name: 'Final Reports & Deliverables',
        description: 'Generating final report outputs',
        defaultProgress: 85,
        services: ['automatic_reports', 'automatic_report', 'report_generation']
    }
];

const normalizeServiceStatus = (serviceData) => {
    const progress = Number(serviceData?.progress || 0);
    const raw = String(serviceData?.status || '').trim().toLowerCase();
    if (raw === 'failed') return 'failed';
    if (raw === 'completed' || progress >= 100) return 'completed';
    if (raw === 'processing' || raw === 'running' || progress > 0) return 'running';
    return 'pending';
};

const clampProgress = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, n));
};

const getServiceData = (services, serviceName) => {
    const data = services?.[serviceName];
    return data && typeof data === 'object' ? data : null;
};

const getFirstServiceData = (services, serviceNames) => {
    for (const name of serviceNames) {
        const data = getServiceData(services, name);
        if (data) return data;
    }
    return null;
};

const getServiceMessage = (serviceData) => String(serviceData?.message || '').trim().toLowerCase();

const GENERIC_PROGRESS_MESSAGES = new Set([
    '',
    'initializing...',
    'waiting...',
    'waiting',
    'in progress',
    'completed'
]);

const isMeaningfulProgressMessage = (message) => {
    const normalized = String(message || '').trim().toLowerCase();
    return normalized.length > 0 && !GENERIC_PROGRESS_MESSAGES.has(normalized);
};

const isServiceStarted = (serviceData) => {
    if (!serviceData || typeof serviceData !== 'object') return false;
    const status = normalizeServiceStatus(serviceData);
    if (status === 'running' || status === 'completed' || status === 'failed') return true;
    return isMeaningfulProgressMessage(getServiceMessage(serviceData));
};

const buildRsaModelOverview = (run, progress) => {
    const services = (progress && typeof progress.services === 'object' && progress.services) || {};
    const runStatus = String(run?.status || '').trim().toLowerCase();
    const createRsaData = getServiceData(services, 'create_rsa');
    const fileTransferData = getServiceData(services, 'file_transfer');
    const finalReportData = getFirstServiceData(services, ['automatic_report', 'automatic_reports', 'report_generation']);
    const analysisServiceNames = [
        'ssd_classification',
        'psd_classification',
        'horizontal',
        'embankment',
        'clear_zone',
        'guardrail',
        'shapefile'
    ];

    const fileTransferProgress = clampProgress(fileTransferData?.progress);
    const createRsaProgress = clampProgress(createRsaData?.progress);
    const createRsaMessage = getServiceMessage(createRsaData);
    const createRsaStarted = isServiceStarted(createRsaData);
    const createRsaCompleted = normalizeServiceStatus(createRsaData) === 'completed';

    const analysisProgressValues = analysisServiceNames
        .map((name) => getServiceData(services, name))
        .filter(Boolean)
        .map((data) => clampProgress(data.progress));
    const analysisAvgProgress = analysisProgressValues.length > 0
        ? Math.round(analysisProgressValues.reduce((a, b) => a + b, 0) / analysisProgressValues.length)
        : 0;
    const analysisStarted = analysisServiceNames.some((name) => isServiceStarted(getServiceData(services, name)));
    const presentAnalysisServices = analysisServiceNames
        .map((name) => getServiceData(services, name))
        .filter(Boolean);
    const analysisCompleted = presentAnalysisServices.length > 0 &&
        presentAnalysisServices.every((data) => normalizeServiceStatus(data) === 'completed');

    const finalReportsProgress = clampProgress(finalReportData?.progress);
    const finalReportsMessage = getServiceMessage(finalReportData);
    const finalReportsStarted = isServiceStarted(finalReportData);
    const automaticReportInstanceLaunched =
        finalReportsStarted &&
        (
            finalReportsMessage.includes('waiting for instance') ||
            finalReportsMessage.includes('sending request to instance') ||
            finalReportsProgress >= 10
        );
    const finalReportsCompleted = normalizeServiceStatus(finalReportData) === 'completed';

    const groupStats = RSA_MODEL_STEP_GROUPS.map((group) => {
        const present = group.services
            .map((name) => ({ name, data: services[name] }))
            .filter((item) => item.data && typeof item.data === 'object');

        const statuses = present.map((item) => normalizeServiceStatus(item.data));
        const progresses = present
            .map((item) => Number(item.data.progress))
            .filter((n) => Number.isFinite(n));
        const avgProgress = progresses.length > 0
            ? Math.round(progresses.reduce((a, b) => a + b, 0) / progresses.length)
            : 0;

        // engine=v2 pre-seeds every stage as 'pending' at run dispatch
        // (via progress_tracker.initialize_run), so a phase with "some done,
        // some pending" used to slip past both `running` and `completed`
        // and the dashboard would render 0%. Treat a phase that has any
        // active/done service (but isn't fully done) as `running`.
        const allCompleted = statuses.length > 0 && statuses.every((s) => s === 'completed');
        const hasAnyActive = statuses.some((s) => s === 'running' || s === 'completed' || s === 'failed');
        return {
            ...group,
            seen: present.length > 0,
            failed: statuses.includes('failed'),
            running: statuses.includes('running') || (hasAnyActive && !allCompleted),
            completed: allCompleted,
            progress: avgProgress
        };
    });

    const hasSeenGroups = groupStats.some((group) => group.seen);
    const hasStartedWork =
        isServiceStarted(fileTransferData) ||
        createRsaStarted ||
        analysisStarted ||
        finalReportsStarted ||
        groupStats.some((group) => group.failed || group.running || group.completed);

    // Treat run as effectively completed if all seen step groups are done,
    // even if the run-level status field hasn't been updated yet (e.g. after video timeout + continue).
    const allGroupsCompleted = hasSeenGroups && groupStats.every((g) => !g.seen || g.completed);
    const isEffectivelyCompleted = runStatus === 'completed' || allGroupsCompleted;

    let phase = 1;
    if (isEffectivelyCompleted || finalReportsCompleted || automaticReportInstanceLaunched) {
        phase = 3;
    } else if (createRsaStarted || createRsaCompleted || analysisStarted) {
        phase = 2;
    }

    const failedStepIndex = groupStats.findIndex((group) => group.failed) + 1;
    const hasFailedStep = failedStepIndex > 0;

    const steps = groupStats.map((group, idx) => {
        const stepIndex = idx + 1;
        let status = 'pending';

        if (isEffectivelyCompleted) {
            status = 'completed';
        } else if (hasFailedStep && stepIndex === failedStepIndex) {
            status = 'failed';
        } else if (runStatus === 'failed' && stepIndex === phase) {
            status = 'failed';
        } else if (stepIndex < phase) {
            status = 'completed';
        } else if (stepIndex === phase) {
            status = 'running';
        }

        let stepProgress = 0;
        if (status === 'completed') {
            stepProgress = 100;
        } else if (status === 'failed') {
            stepProgress = Math.max(group.progress || 0, 90);
        } else if (status === 'running') {
            if (!hasStartedWork) {
                stepProgress = 0;
            } else if (stepIndex === 1) {
                stepProgress = fileTransferProgress > 0 ? fileTransferProgress : group.defaultProgress;
            } else if (stepIndex === 2) {
                const rsaStageProgress = Math.round((createRsaProgress * 0.45) + (analysisAvgProgress * 0.55));
                stepProgress = rsaStageProgress > 0 ? rsaStageProgress : group.defaultProgress;
            } else {
                stepProgress = finalReportsProgress > 0 ? finalReportsProgress : group.defaultProgress;
            }
            if (hasStartedWork) {
                stepProgress = Math.max(5, Math.min(95, stepProgress));
            }
        }

        let liveMessage = '';
        if (status === 'running') {
            if (stepIndex === 1) {
                const candidate = String(fileTransferData?.message || '').trim();
                liveMessage = isMeaningfulProgressMessage(candidate) ? candidate : '';
            } else if (stepIndex === 2) {
                const candidate = String(createRsaData?.message || '').trim();
                liveMessage = isMeaningfulProgressMessage(candidate) ? candidate : '';
                if (!liveMessage) {
                    for (const name of analysisServiceNames) {
                        const d = getServiceData(services, name);
                        const m = String(d?.message || '').trim();
                        if (isMeaningfulProgressMessage(m)) { liveMessage = m; break; }
                    }
                }
            } else {
                const candidate = String(finalReportData?.message || '').trim();
                liveMessage = isMeaningfulProgressMessage(candidate) ? candidate : '';
            }
        }

        // Sub-stages (devs only): one row per service in the group with
        // a known progress entry, so the expanded tile shows the actual
        // pipeline stages (tiling, csf, road_detection, ...) instead of
        // the user-facing rolled-up label. Pending services that haven't
        // emitted yet still appear with status='pending' because
        // initialize_run pre-seeds them (engine=v2). For engine=v1 runs
        // pre-seeding doesn't happen, so only the services that called
        // back show up — that's fine because the user doesn't expand by
        // default.
        const subStages = group.services
            .map((name) => {
                const data = services[name];
                if (!data || typeof data !== 'object') return null;
                const subStatus = normalizeServiceStatus(data);
                const subProgress = clampProgress(data.progress);
                const subMessageRaw = String(data.message || '').trim();
                const subMessage = isMeaningfulProgressMessage(subMessageRaw)
                    ? subMessageRaw
                    : (subStatus === 'completed' ? 'Completed'
                        : subStatus === 'running' ? 'In progress'
                            : subStatus === 'failed' ? 'Failed'
                                : 'Waiting');
                const meta = data.metadata && typeof data.metadata === 'object' ? data.metadata : {};
                const chunksDone = (typeof meta.chunks_done === 'number') ? meta.chunks_done : null;
                const totalChunks = (typeof meta.total_chunks === 'number') ? meta.total_chunks : null;
                return {
                    name,
                    display_name: SERVICE_DISPLAY_NAMES[name] || name,
                    status: subStatus,
                    progress: subProgress,
                    message: subMessage,
                    chunks_done: chunksDone,
                    total_chunks: totalChunks,
                    instance_id: data.instance_id || null
                };
            })
            .filter(Boolean);

        return {
            name: group.name,
            display_name: group.display_name,
            status,
            progress: stepProgress,
            message: status === 'completed'
                ? 'Completed'
                : status === 'running'
                    ? (liveMessage || (hasStartedWork ? 'In progress' : 'Initializing...'))
                    : 'Waiting',
            started_at: null,
            completed_at: status === 'completed' ? new Date() : null,
            duration: null,
            description: group.description,
            instance_id: null,
            folder_id: null,
            folder_name: null,
            sub_stages: subStages
        };
    });

    const completedSteps = steps.filter((s) => s.status === 'completed').length;
    // Smart progress model with 7+ internal milestones so the bar feels gradual:
    // File Transfer (6-44) -> RSA Analysis (45-79) -> Final Reports (80-99) -> 100.
    let overallProgress = 0;
    if (!hasStartedWork && !isEffectivelyCompleted && runStatus !== 'failed') {
        overallProgress = 0;
    } else if (isEffectivelyCompleted) {
        overallProgress = 100;
    } else if (phase === 1) {
        overallProgress = Math.round(6 + ((Math.min(fileTransferProgress, 100) / 100) * 38));
        if (isServiceStarted(fileTransferData)) {
            overallProgress = Math.max(overallProgress, 12);
        }
        if (fileTransferProgress >= 50) {
            overallProgress = Math.max(overallProgress, 28);
        }
        if (fileTransferProgress >= 90) {
            overallProgress = Math.max(overallProgress, 40);
        }
    } else if (phase === 2) {
        const rsaStageProgress = Math.round((createRsaProgress * 0.45) + (analysisAvgProgress * 0.55));
        overallProgress = Math.round(45 + ((Math.min(rsaStageProgress, 100) / 100) * 34));
        if (createRsaStarted) overallProgress = Math.max(overallProgress, 50);
        if (createRsaCompleted) overallProgress = Math.max(overallProgress, 58);
        if (analysisStarted && (createRsaCompleted || analysisAvgProgress >= 15)) overallProgress = Math.max(overallProgress, 66);
        if (analysisCompleted) overallProgress = Math.max(overallProgress, 74);
    } else {
        overallProgress = Math.round(80 + ((Math.min(finalReportsProgress, 100) / 100) * 19));
        if (finalReportsStarted) overallProgress = Math.max(overallProgress, 86);
        if (automaticReportInstanceLaunched) overallProgress = Math.max(overallProgress, 90);
        if (finalReportsCompleted) overallProgress = Math.max(overallProgress, 97);
    }
    overallProgress = clampProgress(overallProgress);

    const overallStatus = runStatus === 'failed'
        ? 'failed'
        : isEffectivelyCompleted
            ? 'completed'
            : steps.some((s) => s.status === 'running')
                ? 'running'
                : 'pending';

    return {
        status: overallStatus,
        progress: {
            percentage: overallStatus === 'completed' ? 100 : overallProgress,
            completed_steps: overallStatus === 'completed' ? steps.length : completedSteps,
            total_steps: steps.length
        },
        steps
    };
};

/**
 * Progress % and step count for dashboard run list tiles — matches main dashboard
 * (RSA model uses three overview groups; pipeline runs use Redis/API progress when present).
 */
export const getRunListProgressStats = (run, progress) => {
    if (!run) return { progressPct: 0, stepsCount: 0 };

    const runProjectType = String(
        run.project_type ||
        run?.metadata?.project_type ||
        ''
    ).trim().toLowerCase();

    if (RSA_MODEL_PROJECT_TYPES.has(runProjectType)) {
        const overview = buildRsaModelOverview(run, progress);
        return {
            progressPct: Math.round(Number(overview.progress.percentage)),
            stepsCount: overview.steps.length
        };
    }

    const hasProgressObj = progress && typeof progress === 'object';
    const rawOverall = hasProgressObj && 'overall_progress' in progress
        ? Number(progress.overall_progress)
        : Number(run.overall_progress ?? 0);
    const progressPct = Number.isFinite(rawOverall) ? Math.round(rawOverall) : 0;

    const totalSvc = hasProgressObj ? Number(progress.total_services) : NaN;
    const stepsFromProgress = Number.isFinite(totalSvc) && totalSvc > 0 ? totalSvc : 0;
    const stepsFromRun = Array.isArray(run.services) ? run.services.length : 0;
    const stepsFromKeys =
        hasProgressObj && progress.services && typeof progress.services === 'object'
            ? Object.keys(progress.services).length
            : 0;
    const stepsCount =
        stepsFromProgress > 0 ? stepsFromProgress : stepsFromRun > 0 ? stepsFromRun : stepsFromKeys;

    return { progressPct, stepsCount };
};

/**
 * Label for the run list tile: current / active pipeline phase from the same steps model as the dashboard.
 * Works for RSA model (grouped steps from {@link buildRsaModelOverview}) and generic runs ({@link transformProgressData}).
 */
export const getDashboardRunActiveStepDisplay = (run, progress) => {
    if (!run) return '';

    const runProjectType = String(
        run.project_type ||
        run?.metadata?.project_type ||
        ''
    ).trim().toLowerCase();

    let steps = [];

    if (RSA_MODEL_PROJECT_TYPES.has(runProjectType)) {
        const overview = buildRsaModelOverview(run, progress);
        steps = overview.steps || [];
    } else {
        const hasProgressObj = progress && typeof progress === 'object';
        const services = hasProgressObj && progress.services && typeof progress.services === 'object'
            ? progress.services
            : null;
        if (services && Object.keys(services).length > 0) {
            const transformed = transformProgressData(progress, {
                run_id: run.run_id,
                project_id: run.project_id,
                project_name: run.project_name,
                created_at: run.created_at,
                user_email: run.user_email,
            });
            steps = transformed.steps || [];
        }
    }

    if (!steps.length) return '';

    const isStepActive = (s) => {
        const st = String(s?.status || '').trim().toLowerCase();
        return st === 'running' || st === 'processing';
    };
    const running = steps.find(isStepActive);
    if (running) return running.display_name || running.name || '';

    const failed = steps.find((s) => s.status === 'failed');
    if (failed) return failed.display_name || failed.name || '';

    const runStatus = String(run?.status || '').trim().toLowerCase();
    if (runStatus === 'completed' || steps.every((s) => s.status === 'completed')) {
        const last = steps[steps.length - 1];
        return last ? (last.display_name || last.name || '') : '';
    }

    const pending = steps.find((s) => s.status === 'pending');
    if (pending) return pending.display_name || pending.name || '';

    return steps[0]?.display_name || steps[0]?.name || '';
};

const AnalysisDashboard = ({
    selectedRun,
    setSelectedRun,
    mockProjectsData,
    displayRuns,
    runProgressMap = {},
    loading = false,
    error = null,
    onBrowseDeliverables = null,
    setActiveMainTab = null
}) => {
    const defaultUserEmail = sessionStorage.getItem('userEmail') || 'demo@example.com';

    // Use props for selectedRun and setSelectedRun, or create local state if not provided
    const [localSelectedRun, setLocalSelectedRun] = useState(null);

    // Which phase's sub-stages are shown in the right-hand inspector pane.
    // Single string (or null) — only one phase is inspected at a time because
    // the inspector occupies a single fixed grid slot. Clicking the active
    // phase's Details collapses it; clicking another phase swaps the content.
    // The reserved slot is what keeps the timeline / running-task / overall
    // progress tiles from moving when the user toggles Details.
    const [activePhase, setActivePhase] = useState(null);
    const toggleStepExpanded = (stepName) => {
        setActivePhase((prev) => (prev === stepName ? null : stepName));
    };
    const currentSelectedRun = selectedRun !== undefined ? selectedRun : localSelectedRun;
    const currentSetSelectedRun = setSelectedRun || setLocalSelectedRun;

    // Get current analysis data (transformed progress data)
    // Use specific run's progress instead of entire runProgress object to prevent infinite loops
    const currentRunProgress = currentSelectedRun ? runProgressMap[currentSelectedRun.run_id] : null;
    const currentAnalysisData = useMemo(() => {
        if (!currentSelectedRun) return null;

        const initiatorEmail =
            currentSelectedRun.user_email ||
            currentSelectedRun?.metadata?.user_email ||
            null;

        const fallbackRunProgress =
            currentSelectedRun?.services_progress &&
            typeof currentSelectedRun.services_progress === 'object' &&
            Object.keys(currentSelectedRun.services_progress).length > 0
                ? {
                    overall_progress: currentSelectedRun.overall_progress ?? 0,
                    services: currentSelectedRun.services_progress,
                    completed_services: Object.values(currentSelectedRun.services_progress).filter((svc) => {
                        const status = String(svc?.status || '').trim().toLowerCase();
                        return status === 'completed' || Number(svc?.progress || 0) >= 100;
                    }).length,
                    total_services: Object.keys(currentSelectedRun.services_progress).length
                }
                : null;
        const progress = currentRunProgress || fallbackRunProgress;
        const runProjectType = String(
            currentSelectedRun.project_type ||
            currentSelectedRun?.metadata?.project_type ||
            ''
        ).trim().toLowerCase();
        const isRsaModelRun = RSA_MODEL_PROJECT_TYPES.has(runProjectType);

        if (isRsaModelRun) {
            const overview = buildRsaModelOverview(currentSelectedRun, progress);
            return {
                id: currentSelectedRun.run_id,
                project_id: currentSelectedRun.project_id,
                project_name: currentSelectedRun.project_name || currentSelectedRun.name,
                status: overview.status,
                progress: overview.progress,
                steps: overview.steps,
                start_time: currentSelectedRun.created_at || currentSelectedRun.last_updated || new Date(),
                initiator_email: initiatorEmail
            };
        }
        
        // Check if progress has meaningful data (has services with data)
        const hasProgressData = progress && 
            progress.services && 
            typeof progress.services === 'object' && 
            Object.keys(progress.services).length > 0;
        
        if (!hasProgressData) {
            // Only show initial steps if services are available
            // If Redis was cleared and this is a new run (< 20 hours), services will be empty
            // In that case, don't show anything (as per user requirement)
            if (!currentSelectedRun.services || currentSelectedRun.services.length === 0) {
                // Check if this is from MongoDB (old task > 20 hours) - if so, we should still show it
                const isOldTask = currentSelectedRun.source === 'mongodb' || currentSelectedRun.data_source === 'mongodb';
                if (!isOldTask) {
                    // New task without services (Redis was cleared) - don't show anything
                    console.warn('⚠️ No services found for run (Redis likely cleared):', currentSelectedRun.run_id);
                    return {
                        id: currentSelectedRun.run_id,
                        project_id: currentSelectedRun.project_id,
                        project_name: currentSelectedRun.name,
                        status: currentSelectedRun.status || 'pending',
                        progress: {
                            percentage: 0,
                            completed_steps: 0,
                            total_steps: 0
                        },
                        steps: [],
                        start_time: currentSelectedRun.created_at || currentSelectedRun.last_updated || new Date(),
                        initiator_email: initiatorEmail
                    };
                }
            }
            
            // Create initial steps from currentSelectedRun.services if available
            // This ensures progress circles appear immediately after launch
            const getServiceDescription = (name) => {
                const descriptions = {
                    'tiling': 'Creating tiles for processing',
                    'csf': 'Classifying ground points',
                    'car_removal': 'Removing vehicles from scene',
                    'road_detection': 'Detecting road boundaries',
                    'embankment': 'Analyzing road embankments',
                    'horizontal': 'Calculating horizontal curves and alignments',
                    'slope_calc': 'Calculating slope values and gradients',
                    'psd_classification': 'Classifying PSD (Particle Size Distribution) data',
                    'automatic_report': 'Generating automatic analysis reports',
                    'report_generation': 'Generating final analysis report'
                };
                return descriptions[name] || `Processing ${name}`;
            };
            
            // Sort services by execution order
            const sortedServices = [...(currentSelectedRun.services || [])].sort((a, b) => {
                const indexA = SERVICE_ORDER.indexOf(a);
                const indexB = SERVICE_ORDER.indexOf(b);
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
            
            const initialSteps = sortedServices.map((serviceName, index) => {
                const isFirst = index === 0;
                
                return {
                    name: serviceName,
                    display_name: SERVICE_DISPLAY_NAMES[serviceName] || serviceName,
                    status: isFirst ? 'running' : 'pending', // First step is running to show orange glow
                    progress: isFirst ? 0 : 0, // First step starts at 0% but is "running"
                    message: isFirst ? 'Initializing...' : 'Waiting...',
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: getServiceDescription(serviceName),
                    instance_id: null,
                    folder_id: null,
                    folder_name: null
                };
            });
            
            // If no services found, log warning but still return structure
            if (initialSteps.length === 0) {
                console.warn('⚠️ No services found for run:', currentSelectedRun.run_id, 'Run data:', currentSelectedRun);
            }
            
            // Return basic structure with initial steps (so analyses-list / steps-timeline / overall-progress show)
            return {
                id: currentSelectedRun.run_id,
                project_id: currentSelectedRun.project_id,
                project_name: currentSelectedRun.project_name || currentSelectedRun.name,
                status: currentSelectedRun.status || 'pending',
                progress: {
                    percentage: currentSelectedRun.overall_progress ?? 0,
                    completed_steps: 0,
                    total_steps: initialSteps.length || 0
                },
                steps: initialSteps,
                start_time: currentSelectedRun.created_at || currentSelectedRun.last_updated || new Date(),
                initiator_email: initiatorEmail
            };
        }
        
        const transformed = transformProgressData(progress, {
            run_id: currentSelectedRun.run_id,
            project_id: currentSelectedRun.project_id,
            project_name: currentSelectedRun.project_name || currentSelectedRun.name,  // Use project_name if available
            created_at: currentSelectedRun.created_at,
            user_email: currentSelectedRun.user_email,
            input_files: []
        });
        
        // If transformed data has no steps but we have services, merge with initial steps
        if ((!transformed.steps || transformed.steps.length === 0) && 
            currentSelectedRun.services && 
            currentSelectedRun.services.length > 0) {
            const getServiceDescription = (name) => {
                const descriptions = {
                    'tiling': 'Creating tiles for processing',
                    'csf': 'Classifying ground points',
                    'car_removal': 'Removing vehicles from scene',
                    'road_detection': 'Detecting road boundaries',
                    'embankment': 'Analyzing road embankments',
                    'horizontal': 'Calculating horizontal curves and alignments',
                    'slope_calc': 'Calculating slope values and gradients',
                    'psd_classification': 'Classifying PSD (Particle Size Distribution) data',
                    'automatic_report': 'Generating automatic analysis reports',
                    'report_generation': 'Generating final analysis report'
                };
                return descriptions[name] || `Processing ${name}`;
            };
            
            const sortedServices = [...(currentSelectedRun.services || [])].sort((a, b) => {
                const indexA = SERVICE_ORDER.indexOf(a);
                const indexB = SERVICE_ORDER.indexOf(b);
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
            
            transformed.steps = sortedServices.map((serviceName, index) => {
                const isFirst = index === 0;
                return {
                    name: serviceName,
                    display_name: SERVICE_DISPLAY_NAMES[serviceName] || serviceName,
                    status: isFirst ? 'running' : 'pending', // First step is running to show orange glow
                    progress: 0,
                    message: isFirst ? 'Initializing...' : 'Waiting...',
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: getServiceDescription(serviceName),
                    instance_id: null,
                    folder_id: null,
                    folder_name: null
                };
            });
            
            transformed.progress = {
                ...transformed.progress,
                total_steps: transformed.steps.length
            };
        }
        
        return { ...transformed, initiator_email: initiatorEmail };
    }, [currentSelectedRun, currentRunProgress]);

    // Auto-select first run on load
    // TEMPORARILY DISABLED: Commented out to prevent server overload while component is WIP
    // useEffect(() => {
    //     if (runs.length > 0 && !selectedRun) {
    //         setSelectedRun(runs[0]);
    //     }
    // }, [runs, selectedRun]);

    // Removed mock data - using real data from hooks
    const mockAnalysesData = [ // Keep for reference but not used
        {
            id: 'analysis_123',
            project_name: 'CFX SR-538 Poinciana Pkwy',
            project_id: 'project_789',
            status: 'running',
            start_time: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            estimated_completion: new Date(Date.now() + 1 * 60 * 60 * 1000), // 1 hour from now
            progress: {
                percentage: 65,
                completed_steps: 3,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Creating tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Classifying ground points'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'running',
                    progress: 75,
                    started_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
                    completed_at: null,
                    duration: null,
                    description: 'Removing vehicles from scene'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Detecting road boundaries'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Analyzing road embankments'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Generating final analysis report'
                }
            ],
            input_files: ['highway_section_1.las', 'highway_section_2.las'],
            user_id: 'user_456',
            project_id: 'project_789'
        },
        {
            id: 'analysis_456',
            project_name: 'Urban Road Analysis',
            project_id: 'project_789',
            status: 'completed',
            start_time: new Date(Date.now() - 4 * 60 * 60 * 1000),
            completed_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
            progress: {
                percentage: 100,
                completed_steps: 6,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 4 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Creating tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 3 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Classifying ground points'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 3 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 2.5 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Removing vehicles from scene'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 2.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Detecting road boundaries'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Analyzing road embankments'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 1.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
                    duration: 1800,
                    description: 'Generating final analysis report'
                }
            ],
            input_files: ['urban_road_1.las', 'urban_road_2.las'],
            user_id: 'user_456',
            project_id: 'project_789'
        },
        {
            id: 'analysis_789',
            project_name: 'ADOT Climbing Passing RSA',
            project_id: 'project_452',
            status: 'running',
            start_time: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
            estimated_completion: new Date(Date.now() + 3 * 60 * 60 * 1000), // 3 hours from now
            progress: {
                percentage: 25,
                completed_steps: 1,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 30 * 60 * 1000),
                    completed_at: new Date(Date.now() - 25 * 60 * 1000),
                    duration: 300, // 5 minutes
                    description: 'Creating tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'running',
                    progress: 50,
                    started_at: new Date(Date.now() - 25 * 60 * 1000),
                    completed_at: null,
                    duration: null,
                    description: 'Classifying ground points'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Removing vehicles from scene'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Detecting road boundaries'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Analyzing road embankments'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Generating final analysis report'
                }
            ],
            input_files: ['bridge_section.las'],
            user_id: 'user_456',
            project_id: 'project_452'
        },
        {
            id: 'analysis_486',
            project_name: 'ADOT West Kingman Interchange',
            project_id: 'project_486',
            status: 'pending',
            start_time: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
            estimated_completion: new Date(Date.now() + 4 * 60 * 60 * 1000), // 4 hours from now
            progress: {
                percentage: 0,
                completed_steps: 0,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Creating tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Classifying ground points'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Removing vehicles from scene'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Detecting road boundaries'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Analyzing road embankments'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'pending',
                    progress: 0,
                    started_at: null,
                    completed_at: null,
                    duration: null,
                    description: 'Generating final analysis report'
                }
            ],
            input_files: ['west_kingman.las'],
            user_id: 'user_456',
            project_id: 'project_486'
        },
        {
            id: 'analysis_416',
            project_name: 'CFX SR 516 Lake-Orange County',
            project_id: 'project_416',
            status: 'completed',
            start_time: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
            completed_at: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
            progress: {
                percentage: 100,
                completed_steps: 6,
                total_steps: 6
            },
            steps: [
                {
                    name: 'tiling',
                    display_name: 'File Tiling',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 6 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 5.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Creating tiles for processing'
                },
                {
                    name: 'csf',
                    display_name: 'Ground Classification',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 5.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Classifying ground points'
                },
                {
                    name: 'car_removal',
                    display_name: 'Vehicle Removal',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 4.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Removing vehicles from scene'
                },
                {
                    name: 'road_detection',
                    display_name: 'Road Detection',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 4.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 4 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Detecting road boundaries'
                },
                {
                    name: 'embankment',
                    display_name: 'Embankment Analysis',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 4 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Analyzing road embankments'
                },
                {
                    name: 'report_generation',
                    display_name: 'Report Generation',
                    status: 'completed',
                    progress: 100,
                    started_at: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
                    completed_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
                    duration: 1800, // 30 minutes
                    description: 'Generating final analysis report'
                }
            ],
            input_files: ['sr_516_lake_orange.las'],
            user_id: 'user_456',
            project_id: 'project_416'
        }
    ];

    // Use provided mockProjectsData or default
    const defaultMockProjectsData = [
        {
            id: 'project_789',
            name: 'CFX SR-538 Poinciana Pkwy',
            description: 'Comprehensive analysis of highway sections for maintenance planning',
            total_analyses: 2,
            completed_analyses: 1,
            running_analyses: 1,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
        {
            id: 'project_452',
            name: 'ADOT Climbing Passing RSA',
            description: 'Structural analysis of bridge components using LiDAR data',
            total_analyses: 1,
            completed_analyses: 0,
            running_analyses: 1,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
        {
            id: 'project_486',
            name: 'ADOT West Kingman Interchange',
            description: 'Structural analysis of bridge components using LiDAR data',
            total_analyses: 1,
            completed_analyses: 0,
            running_analyses: 0,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
        {
            id: 'project_416',
            name: 'CFX SR 516 Lake-Orange County',
            description: 'Structural analysis of bridge components using LiDAR data',
            total_analyses: 1,
            completed_analyses: 1,
            running_analyses: 0,
            last_updated: new Date(Date.now() - 30 * 60 * 1000)
        },
    ];
    
    const projectsData = mockProjectsData || defaultMockProjectsData;

    // No need for useEffect - data loaded via hooks



    const getStatusClass = (status) => {
        switch (status) {
            case 'completed':
                return 'status-completed';
            case 'processing':
            case 'running':
                return 'status-running';
            case 'failed':
                return 'status-failed';
            case 'pending':
                return 'status-pending';
            default:
                return 'status-pending';
        }
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
        });
    };

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    const formatDate = (date) => {
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h ago`;
        
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d ago`;
        
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };



    // Use mock data for GUI preview (no API calls or polling)
    // Convert mockAnalysesData to runs format for compatibility
    const mockRuns = mockAnalysesData.map(analysis => ({
        run_id: analysis.id,
        project_id: analysis.project_id,
        project_name: analysis.project_name,
        name: analysis.project_name,
        status: analysis.status,
        created_at: analysis.start_time,
        user_email: defaultUserEmail,
        services: analysis.steps.map(step => step.name),
        overall_progress: analysis.progress.percentage
    }));

    // Use provided displayRuns or mockRuns
    const runsToUse = displayRuns || mockRuns;
    const displayLoading = Boolean(loading);
    const displayError = error;

    // Auto-select first run if none selected
    useEffect(() => {
        if (runsToUse.length > 0 && !currentSelectedRun) {
            currentSetSelectedRun(runsToUse[0]);
        }
    }, [runsToUse.length, currentSetSelectedRun, currentSelectedRun]);

    // Use currentAnalysisData (from API/Redis run + services) so real runs show steps-timeline and overall-progress.
    // Fall back to mock only when selected run matches mock id (e.g. demo data).
    const selectedAnalysisFromMock = currentSelectedRun ? mockAnalysesData.find(a => a.id === currentSelectedRun.run_id) : null;
    const displayAnalysisData = currentAnalysisData || (selectedAnalysisFromMock ? {
        id: selectedAnalysisFromMock.id,
        project_id: selectedAnalysisFromMock.project_id,
        project_name: selectedAnalysisFromMock.project_name,
        status: selectedAnalysisFromMock.status,
        progress: selectedAnalysisFromMock.progress,
        steps: selectedAnalysisFromMock.steps,
        start_time: selectedAnalysisFromMock.start_time || currentSelectedRun.created_at || null,
        initiator_email: currentSelectedRun?.user_email || null
    } : null);

    const rsaModelPayloadAnalysisLabels = useMemo(() => {
        const pt = String(
            currentSelectedRun?.project_type ||
            currentSelectedRun?.metadata?.project_type ||
            ''
        ).trim().toLowerCase();
        if (pt !== 'rsa-model') return [];
        return getRsaModelPayloadAnalysisLabels(currentSelectedRun);
    }, [currentSelectedRun]);

    // Format date helper
    const formatDateTime = (date) => {
        if (!date) return 'N/A';
        const d = date instanceof Date ? date : new Date(date);
        return d.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // Return only the main content area (without wrapper and projects panel)
    return (
        <div className="main-content-area-dashboard">
            {displayLoading ? (
                <div className="analysis-dashboard-loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading Analysis Dashboard...</p>
                </div>
            ) : displayError ? (
                <div className="dashboard-error">
                    <p>Error: {displayError}</p>
                </div>
            ) : !displayAnalysisData ? (
                <div className="analyses-container">
                    <div className="no-analyses">
                        <p>No analysis selected. Please select a project from the right panel.</p>
                    </div>
                </div>
            ) : (() => {
                const activeStepObj = activePhase
                    ? displayAnalysisData.steps.find(
                          (s) => s.name === activePhase && Array.isArray(s.sub_stages) && s.sub_stages.length > 0
                      ) || null
                    : null;
                return (
                <div className="analyses-container">
                    <div className="analyses-list analyses-list--grid">
                        {/* Project Header — spans both columns */}
                        <div className="dashboard-tile-bg dashboard-hero-tile">
                            <div className="analysis-dashboard-project-header">
                                <h4 title={displayAnalysisData.project_name}>{displayAnalysisData.project_name}</h4>
                                <span className={`project-status ${getStatusClass(displayAnalysisData.status)}`}>
                                    {displayAnalysisData.status}
                                </span>
                            </div>
                        </div>

                        {/* Steps Timeline — left column. Clicking Details on a phase
                            sets activePhase and populates the right-column inspector
                            without displacing any other tile. */}
                        <div className="steps-timeline-container dashboard-tile-bg">
                            <div className="steps-timeline">
                                {displayAnalysisData.steps.map((step, index) => {
                                    const phaseLogsUrl = buildLogsInsightsUrl(
                                        displayAnalysisData.id,
                                        STEP_LOG_GROUPS[step.name] || []
                                    );
                                    const subStages = Array.isArray(step.sub_stages) ? step.sub_stages : [];
                                    const hasSubStages = subStages.length > 0;
                                    const isActive = activePhase === step.name;
                                    return (
                                        <div
                                            key={step.name}
                                            className={`step-item ${getStatusClass(step.status)}`}
                                            data-active={isActive ? 'true' : 'false'}
                                        >
                                            <div className="step-status">
                                                <span className="status-text">{step.status}</span>
                                            </div>
                                            <div className="step-circle-wrapper">
                                                <div className="step-icon">
                                                    <span className="step-number">{index + 1}</span>
                                                </div>
                                            </div>
                                            <div className="step-info">
                                                <h4>{step.display_name}</h4>
                                                <div className="step-info-actions">
                                                    {hasSubStages && (
                                                        <button
                                                            type="button"
                                                            className="step-expand-toggle"
                                                            onClick={() => toggleStepExpanded(step.name)}
                                                            aria-expanded={isActive}
                                                            title={isActive ? 'Hide pipeline stages' : 'Show pipeline stages in the inspector'}
                                                        >
                                                            <i className={`fas fa-chevron-${isActive ? 'up' : 'right'}`}></i>
                                                            {isActive ? ' Hide details' : ` Details (${subStages.length})`}
                                                        </button>
                                                    )}
                                                    {phaseLogsUrl && (
                                                        <a
                                                            href={phaseLogsUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="step-logs-link"
                                                            title={`Open CloudWatch Logs for ${step.display_name} stages, filtered by this run`}
                                                        >
                                                            <i className="fas fa-external-link-alt"></i> View logs
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Phase Inspector — right column, ALWAYS rendered. Empty
                            state when no phase is active; sub-stages list when one
                            is. This dedicated slot is what guarantees the timeline,
                            running-task, and overall-progress tiles never move when
                            Details is toggled. */}
                        <aside
                            className={`phase-inspector dashboard-tile-bg ${activeStepObj ? `phase-inspector--active ${getStatusClass(activeStepObj.status)}` : 'phase-inspector--empty'}`}
                            aria-live="polite"
                        >
                            {activeStepObj ? (
                                <>
                                    <div className="phase-inspector-header">
                                        <i className="fas fa-microchip"></i>
                                        <span className="phase-inspector-title">{activeStepObj.display_name}</span>
                                        <span className="phase-inspector-count">
                                            {activeStepObj.sub_stages.length} pipeline stages
                                        </span>
                                        <button
                                            type="button"
                                            className="phase-inspector-close"
                                            onClick={() => setActivePhase(null)}
                                            aria-label="Close pipeline stages inspector"
                                            title="Close inspector"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                    <div className="phase-inspector-body">
                                        {activeStepObj.sub_stages.map((sub) => {
                                            const subLogsUrl = buildLogsInsightsUrl(
                                                displayAnalysisData.id,
                                                STEP_LOG_GROUPS[sub.name] || []
                                            );
                                            const chunkSuffix = (sub.total_chunks && sub.chunks_done != null)
                                                ? ` (${sub.chunks_done}/${sub.total_chunks})`
                                                : '';
                                            return (
                                                <div key={sub.name} className={`sub-stage-row ${getStatusClass(sub.status)}`}>
                                                    <div className="sub-stage-name">
                                                        <span className="sub-stage-label">{sub.display_name}</span>
                                                        <span className="sub-stage-status">{sub.status}{chunkSuffix}</span>
                                                    </div>
                                                    <div className="sub-stage-bar-container">
                                                        <div
                                                            className={`sub-stage-bar-fill ${getStatusClass(sub.status)}`}
                                                            style={{ width: `${sub.progress}%` }}
                                                        />
                                                    </div>
                                                    <div className="sub-stage-meta">
                                                        <span className="sub-stage-progress-text">{sub.progress}%</span>
                                                        <span className="sub-stage-message" title={sub.message}>{sub.message}</span>
                                                        {subLogsUrl ? (
                                                            <a
                                                                href={subLogsUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="sub-stage-logs-link"
                                                                title={`Open CloudWatch Logs for ${sub.display_name}, filtered by this run`}
                                                            >
                                                                <i className="fas fa-external-link-alt"></i> Logs
                                                            </a>
                                                        ) : (
                                                            <span className="sub-stage-logs-link sub-stage-logs-link--unavailable" title="No CloudWatch log group available for this stage (e.g. Lambda-only step)">
                                                                <i className="fas fa-external-link-alt"></i> &mdash;
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="phase-inspector-empty-state">
                                    <i className="fas fa-microchip phase-inspector-empty-icon" aria-hidden="true"></i>
                                    <p className="phase-inspector-empty-title">Pipeline Inspector</p>
                                    <p className="phase-inspector-empty-hint">
                                        Pick a phase&rsquo;s <strong>Details</strong> button to drill into its pipeline stages here&mdash;without losing sight of the run.
                                    </p>
                                </div>
                            )}
                        </aside>

                        {/* Running Task — left column, always visible. */}
                        <div className="analysis-dashboard-task-info analysis-dashboard-task-info--running dashboard-tile-bg">
                            <div className="task-info-details-header-container">
                                <h5 className="task-info-details-header">Running Task(s):</h5>
                                <div className="task-info-details-tags">
                                    {rsaModelPayloadAnalysisLabels.length > 0 ? (
                                        rsaModelPayloadAnalysisLabels.map((label, idx) => (
                                            <span key={`${label}-${idx}`} className="summary-tag">
                                                {label}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="summary-tag">
                                            {displayAnalysisData.steps?.find((s) => s.status === 'running')?.display_name ||
                                                displayAnalysisData.steps?.[0]?.display_name ||
                                                'Analysis'}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="task-info-item">
                                <span className="task-info-label">
                                    <i className="fas fa-hashtag"></i>
                                    Task ID:
                                </span>
                                <span className="task-info-value">
                                    {displayAnalysisData.id}
                                    {buildSfConsoleUrl(displayAnalysisData.id) && (
                                        <a
                                            href={buildSfConsoleUrl(displayAnalysisData.id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="task-info-aws-link"
                                            title="Open this run in the AWS Step Functions console"
                                            style={{ marginLeft: 8 }}
                                        >
                                            <i className="fas fa-external-link-alt"></i> SF console
                                        </a>
                                    )}
                                </span>
                            </div>
                            <div className="task-info-item">
                                <span className="task-info-label">
                                    <i className="fas fa-user"></i>
                                    Initiator:
                                </span>
                                <span className="task-info-value">
                                    {displayAnalysisData.initiator_email || '—'}
                                </span>
                            </div>
                            <div className="task-info-item">
                                <span className="task-info-label">
                                    <i className="fas fa-clock"></i>
                                    Start Time:
                                </span>
                                <span className="task-info-value">{formatDateTime(displayAnalysisData.start_time)}</span>
                            </div>
                        </div>

                        {/* Overall Progress — spans both columns */}
                        <div className="overall-progress dashboard-tile-bg dashboard-overall-progress-tile">
    <div className="progress-header">
        <span>Overall Progress</span>

        {(() => {
            const activeStep = displayAnalysisData.steps?.find((s) => {
                const stepStatus = String(s?.status || '').trim().toLowerCase();
                return stepStatus === 'running' || stepStatus === 'processing';
            });

            const genericMsgs = new Set([
                'Initializing...',
                'In progress',
                'Waiting',
                'Completed',
                ''
            ]);

            const liveMsg =
                activeStep?.message && !genericMsgs.has(activeStep.message)
                    ? activeStep.message
                    : null;

            const fallback = activeStep
                ? activeStep.description || activeStep.display_name
                : `${displayAnalysisData.progress.completed_steps} of ${displayAnalysisData.progress.total_steps} steps completed`;

            const text = liveMsg || fallback;

            return (
                <span className="progress-text" key={text}>
                    {text}
                </span>
            );
        })()}

        <span className="progress-percentage">
            {displayAnalysisData.progress.percentage}%
        </span>
    </div>

    <div className="progress-bar-container">
        <div
            className={`progress-bar-fill ${getStatusClass(displayAnalysisData.status)}`}
            style={{ width: `${displayAnalysisData.progress.percentage}%` }}
        ></div>
    </div>
    {setActiveMainTab && (
        <div className="dashboard-nav-footer">
            <button
                className="rsa-wizard-nav-btn rsa-wizard-nav-btn-ghost"
                onClick={() => setActiveMainTab('routes')}
            >
                <i className="fas fa-arrow-left"></i> Back: Tasks &amp; Routes
            </button>
            <button
                className="rsa-wizard-nav-btn rsa-wizard-nav-btn-primary"
                onClick={() => setActiveMainTab('deliverables')}
            >
                Next: Deliverables <i className="fas fa-arrow-right"></i>
            </button>
        </div>
    )}
</div>
                    </div>
                </div>
                );
            })()}
        </div>
    );
};

export default AnalysisDashboard;
